import React, {useEffect, useRef, useState} from "react";

import useDebounce from "hooks/useDebounce";
import {Button} from "primereact/button";
import {Calendar, CalendarProps} from "primereact/calendar";
import {FormEvent} from "primereact/ts-helpers";
import {T} from "services/lang/LanguageService";
import {DEFAULT_CALENDAR_FORMAT} from "utils/constants";
import {JodaTimeUtils} from "utils/joda-time-utils";
import {ChronoUnitLegacy} from "utils/time-utils";
import { ChronoUnit, Instant } from "@js-joda/core";
import { useCompanyTimezone, useDisplayTimezone } from "hooks/useTimezone";

export type CalendarWrapperProps = {
  value: Date | undefined;
  onChange(date: Date): void;
  onInstantChange?(instant: Instant): void;
  dateFrequency?: ChronoUnitLegacy;
  debounceTimeInMs?: number;
} & Omit<CalendarProps, "value" | "onChange">;

const CalendarWrapper = ({
  value,
  onChange,
  onInstantChange,
  dateFrequency,
  debounceTimeInMs,
  ...props
}: CalendarWrapperProps) => {
  const displayTz = useDisplayTimezone();
  const companyTz = useCompanyTimezone();


  const valueRef = useRef<Date | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [instant, setInstant] = useState<Instant | null>(null);
  const debouncedValue = useDebounce(date, { delay: debounceTimeInMs });

  useEffect(() => {
    const handleEnterPressed = (event: KeyboardEvent) =>
      (event.code === "Enter" || event.code === "NumpadEnter") && replaceDateValue();

    window.addEventListener("keydown", handleEnterPressed);

    return () => window.removeEventListener("keydown", handleEnterPressed);
  }, []);

  useEffect(() => {
    debouncedValue && mainOnChange(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    valueRef.current && (valueRef.current = null);
  }, [value]);

  const mainOnChange = (newDate: Date) => {
    onChange(newDate);
    onInstantChange?.(newDate.toInstant());
  }

  const mainSetDate = (newDate: Date) => {
    setDate(newDate);
    setInstant(newDate.toInstant());
  }

  const replaceDateValue = () => {
    if (valueRef.current != null && value !== valueRef.current) {
      mainOnChange(valueRef.current);
    }
  };

  const onCalendarChange = (e: FormEvent<Date, React.SyntheticEvent<Element, Event>>) => {
    if (!e.value) return;

    const pickedDate = JodaTimeUtils.truncateTo(e.value as Date, ChronoUnit.MINUTES, companyTz);
  
    if (e.originalEvent?.type === "input") {
      valueRef.current = pickedDate;
    } else {
      mainSetDate(pickedDate)
      mainOnChange(pickedDate);
    }
  };
  
  const calendarFooterTemplate = () => {
    return (
      <div className="flex flex-row justify-content-between">
        <Button
          type="button"
          label={T("action.truncate-hours")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => {
            if (!value) return;
            const truncatedDate = JodaTimeUtils.truncateTo(value as Date, ChronoUnit.DAYS, companyTz);
            mainOnChange(truncatedDate);
          }}
          />
        <Button
          type="button"
          label={T("action.truncate-minutes")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => {
            if (!value) return;
            const truncatedDate = JodaTimeUtils.truncateTo(value as Date, ChronoUnit.HOURS, companyTz);
            mainOnChange(truncatedDate);
          }}
        />
      </div>
    );
  };

  return (
    <Calendar
      value={value}
      onChange={onCalendarChange}
      footerTemplate={props.showTime === false ? undefined : calendarFooterTemplate}
      showIcon
      iconPos="left"
      dateFormat={DEFAULT_CALENDAR_FORMAT}
      showTime={props.showTime ?? true}
      onBlur={replaceDateValue}
      className={`p-inputtext-sm ${props.className || ""}`}
      {...props}
    />
  );
};

export default CalendarWrapper;
